/**
 * Curated help targets the voice assistant can highlight.
 * Keys are stable ids from the BE help catalogue / guides / ui.highlight.
 *
 * Prefer existing `data-testid`s from V2 session UI. Targets that only exist
 * inside an open menu are still listed — guides should open the parent first.
 */
export const HELP_TARGET_SELECTORS = {
  // ── Sidebar ──────────────────────────────────────────────────────────
  'nav.sessions': '[data-testid="sidebar-link-view-sessions"]',
  'nav.new-session': '[data-testid="sidebar-link-new-session"]',
  'nav.last-session': '[data-testid="sidebar-link-open-last-session"]',
  'nav.templates': '[data-testid="sidebar-link-templates"]',
  // Profile avatar opens the account menu where Settings lives on the new sidebar.
  'nav.profile':
    '[data-testid="user-dropdown-trigger"], [data-testid="sidebar-user-account-menu"]',
  // Legacy top-level settings link OR item inside the profile dropdown.
  'nav.settings':
    '[data-testid="sidebar-link-settings"], [data-testid="sidebar-menu-settings"]',
  'nav.evidence': '[data-testid="sidebar-link-view-evidence"]',
  'nav.patients': '[data-testid="sidebar-link-patients"]',
  'nav.team': '[data-testid="sidebar-link-team"], [data-testid="sidebar-menu-team"]',
  'nav.organization': '[data-testid="sidebar-link-organization"]',
  'nav.inbox': '[data-testid="sidebar-link-inbox"]',
  'nav.integrations': '[data-testid="sidebar-link-integrations"]',
  'nav.protocols': '[data-testid="sidebar-link-protocols"]',
  'nav.calculators': '[data-testid="sidebar-link-calculators"]',
  'nav.coding': '[data-testid="sidebar-link-view-coding-dashboard"]',
  'nav.support': '[data-testid="sidebar-footer-get-support"]',

  // ── Templates library ────────────────────────────────────────────────
  'templates.create': '[data-testid="create-template-button"]',
  'templates.search': '[data-testid="template-search-input"]',

  // ── Settings / Defaults ──────────────────────────────────────────────
  'settings.voice-style': '[data-testid="settings-default-style"]',
  'settings.note-template': '[data-testid="default-note-template-field"]',
  'settings.scribe-fast': '[data-testid="scribe-type-card-LEFT"]',
  'settings.scribe-best': '[data-testid="scribe-type-card-RIGHT"]',

  // ── Session header: tabs / create / mic / start ──────────────────────
  'session.tabs': '[data-testid="v2-artifact-tabs"]',
  'session.tab-note': '[data-testid="v2-artifact-tab-note"]',
  'session.tab-context': '[data-testid="v2-artifact-tab-context"]',
  'session.tab-transcript': '[data-testid="v2-artifact-tab-transcript"]',
  'session.create': '[data-testid="v2-artifact-tabs-create"]',
  'session.tabs-switcher': '[data-testid="v2-artifact-tabs-switcher"]',
  'session.create-document': '[data-testid="v2-create-document-option"]',
  'session.create-letter': '[data-testid="v2-create-letter-option"]',
  'session.panel-toggle': '[data-testid="v2-session-panel-toggle"]',
  // Every mic control the app can put on screen, because the one that exists
  // depends on the shell: V2 session header/footer, the V1 session-panel header
  // (desktop popover or the web select), and mini mode. Listing only the V2
  // trigger made "change my microphone" a target_missing on every other
  // surface, which the model then reported as "I can't do that".
  'session.mic':
    '[data-testid="v2-input-source-trigger"], [data-testid="tauri-mic-selector-trigger"], [data-testid="mic-selector-trigger"], [data-testid="mini-mode-mic-picker"]',
  'session.transcription-controls': '[data-testid="v2-transcription-controls"]',
  'session.start':
    '[data-testid="start-recording-button"], [data-testid="v2-record-fab-start"]',
  'session.stop':
    '[data-testid="stop-recording-button"], [data-testid="v2-record-fab-stop"]',
  'session.pause': '[data-testid="pause-recording-button"]',
  'session.resume':
    '[data-testid="resume-recording-button"], [data-testid="v2-record-fab-resume"]',

  // Back-compat aliases used by earlier help catalogue / guides
  'session.note-tab': '[data-testid="v2-artifact-tab-note"]',
  'session.generate-note': '[data-testid="v2-session-card-prechart-generation-row"]',
  'session.template-picker':
    '[data-testid="v2-note-context-card-template-row"], [data-testid="v2-note-context-card-document-template-row"]',

  // ── Session side panel / session card ────────────────────────────────
  'session.panel': '[data-testid="v2-session-panel"]',
  'session.card': '[data-testid="v2-session-card"]',
  'session.title': '[data-testid="v2-session-card-summary-row"]',
  'session.patient': '[data-testid="v2-session-card-patient-link-row"]',
  'session.language': '[data-testid="v2-session-card-language-row"]',
  'session.datetime': '[data-testid="v2-session-card-date-time-row"]',
  'session.status': '[data-testid="v2-session-card-status-row"]',
  'session.share': '[data-testid="v2-session-card-share-row"]',

  // ── Note context card ────────────────────────────────────────────────
  'session.note-card': '[data-testid="v2-note-context-card"]',
  'session.template-row':
    '[data-testid="v2-note-context-card-template-row"], [data-testid="v2-note-context-card-document-template-row"]',
  'session.style-row': '[data-testid="v2-note-context-card-style-row"]',
  'session.style-brief': '[data-testid="v2-note-context-card-style-option-BRIEF"]',
  'session.style-detailed': '[data-testid="v2-note-context-card-style-option-DETAILED"]',
  'session.style-super-detailed':
    '[data-testid="v2-note-context-card-style-option-SUPER_DETAILED"]',
  'session.more-options': '[data-testid="v2-note-context-card-more-options"]',
  'session.regenerate': '[data-testid="v2-note-context-card-regenerate-menu-item"]',
  'session.copy-note': '[data-testid="v2-note-context-card-copy"]',
  'session.export': '[data-testid="v2-note-context-card-export-row"]',
  'session.verify': '[data-testid="v2-note-context-card-verify-row"]',

  // ── Other session panel cards ────────────────────────────────────────
  'session.tasks': '[data-testid="v2-tasks-card"]',
  'session.tasks-create': '[data-testid="v2-tasks-card-create-button"]',
  'session.tools': '[data-testid="v2-tools-card"]',
} as const;

export type HelpTargetId = keyof typeof HELP_TARGET_SELECTORS;

export const isHelpTargetId = (value: string): value is HelpTargetId =>
  Object.prototype.hasOwnProperty.call(HELP_TARGET_SELECTORS, value);

/** Prefer an on-screen match when several nodes share a selector (e.g. header + mobile footer). */
const isVisuallyHighlightable = (el: HTMLElement): boolean => {
  if (el.closest('[aria-hidden="true"]')) return false;
  const style = window.getComputedStyle(el);
  if (
    style.display === 'none' ||
    style.visibility === 'hidden' ||
    style.opacity === '0'
  ) {
    return false;
  }
  const box = el.getBoundingClientRect();
  return box.width > 0 && box.height > 0;
};

export const resolveHelpTargetElement = (target: string): HTMLElement | null => {
  if (!isHelpTargetId(target)) return null;
  const selector = HELP_TARGET_SELECTORS[target];
  const nodes = document.querySelectorAll(selector);
  let fallback: HTMLElement | null = null;
  for (const node of nodes) {
    if (!(node instanceof HTMLElement)) continue;
    fallback ??= node;
    if (isVisuallyHighlightable(node)) return node;
  }
  return fallback;
};
